import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { markerVisual, normalizedRideRole } from './map-identity';

const configuredStyle = import.meta.env.VITE_OSM_DARK_STYLE_URL?.trim();
const configuredTileUrl = import.meta.env.VITE_OSM_TILE_URL?.trim();
const useCustomStyle = import.meta.env.VITE_USE_CUSTOM_MAP_STYLE === 'true' && Boolean(configuredStyle);
const tacticalRasterStyle = {
  version: 8,
  glyphs: 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf',
  sources: {
    tacticalTiles: {
      type: 'raster',
      tiles: [
        configuredTileUrl || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors © CARTO',
    },
  },
  layers: [
    { id: 'tactical-background', type: 'background', paint: { 'background-color': '#040810' } },
    {
      id: 'tactical-raster',
      type: 'raster',
      source: 'tacticalTiles',
      paint: {
        'raster-opacity': 0.9,
        'raster-contrast': 0.22,
        'raster-saturation': -0.8,
        'raster-brightness-min': 0.05,
        'raster-brightness-max': 0.42,
      },
    },
  ],
};
const initialStyle = useCustomStyle ? configuredStyle : tacticalRasterStyle;
const emptyCollection = () => ({ type: 'FeatureCollection', features: [] });
const woodPatternImage = {
  width: 2,
  height: 2,
  data: new Uint8Array([
    20, 29, 39, 255, 24, 35, 46, 255,
    24, 35, 46, 255, 20, 29, 39, 255,
  ]),
};

function validCoordinates(value) {
  return Array.isArray(value)
    && value.length >= 2
    && Number.isFinite(Number(value[0]))
    && Number.isFinite(Number(value[1]))
    && Math.abs(Number(value[0])) <= 180
    && Math.abs(Number(value[1])) <= 90;
}

function pointCoordinates(item) {
  const value = item?.payload && typeof item.payload === 'object' ? item.payload : item;
  const candidates = [
    value?.location?.coordinates,
    value?.coordinates,
    value?.position?.coordinates,
    value?.lastReliablePosition?.coordinates,
  ];
  for (const candidate of candidates) {
    if (validCoordinates(candidate)) return candidate.slice(0, 2).map(Number);
  }
  const reliable = value?.lastReliablePosition;
  const longitude = Number(value?.longitude ?? value?.lng ?? value?.lon ?? reliable?.longitude);
  const latitude = Number(value?.latitude ?? value?.lat ?? reliable?.latitude);
  return Number.isFinite(longitude) && Number.isFinite(latitude)
    && Math.abs(longitude) <= 180 && Math.abs(latitude) <= 90
    ? [longitude, latitude]
    : null;
}

function destinationCoordinates(trip) {
  const coordinates = trip?.destination?.coordinates;
  return validCoordinates(coordinates) ? coordinates.slice(0, 2).map(Number) : null;
}

function memberFeatures(locations) {
  return locations.flatMap((item) => {
    const coordinates = pointCoordinates(item);
    if (!coordinates) return [];
    const memberId = String(item.memberId || item.member?.id || '');
    const tripId = String(item.tripId || item.trip?.id || '');
    return [{
      type: 'Feature',
      id: `${tripId}:${memberId}`,
      geometry: { type: 'Point', coordinates },
      properties: {
        memberId,
        tripId,
        memberName: item.member?.name || `Member ${memberId.slice(-6)}`,
        clubId: String(item.member?.clubId || item.clubId || item.trip?.groupId || ''),
        username: item.member?.username || '',
        tripName: item.trip?.name || '',
        destinationLabel: item.trip?.destination?.label || '',
        speedKmh: Number(item.speedKmh) || 0,
        accuracyMeters: Number(item.accuracyMeters) || 0,
        updatedAt: item.updatedAt || item.receivedAt || item.recordedAt || '',
        rideRole: normalizedRideRole(item.rideRole || item.member?.rideRole || 'member'),
        heading: Number(item.heading ?? item.bearing ?? 0),
        isSpeaking: item.voiceTransmitting === true || item.member?.voiceTransmitting === true,
        safeConfirmed: Boolean(item.safeConfirmedAt || item.member?.safeConfirmedAt),
        subConvoyId: String(item.subConvoyId || item.member?.subConvoyId || ''),
        subConvoyName: item.subConvoyName || item.member?.subConvoyName || '',
        clubPattern: item.clubPattern || item.member?.clubPattern || '',
        rideStatus: item.rideStatus || item.member?.rideStatus || 'riding',
        connectivityStage: item.connectivityStage || item.member?.connectivityStage || 'online',
        batteryPercent: Number(item.deviceHealth?.batteryPercent ?? item.member?.deviceHealth?.batteryPercent ?? -1),
        isCharging: item.deviceHealth?.isCharging === true,
      },
    }];
  });
}

function tripFeatures(locations) {
  const trips = new Map();
  for (const item of locations) {
    const trip = item.trip;
    const tripId = trip?.id || String(item.tripId || '');
    if (!tripId || trips.has(tripId) || !destinationCoordinates(trip)) continue;
    trips.set(tripId, { item, trip });
  }
  const destinations = [];
  const routes = [];
  for (const [tripId, { item, trip }] of trips) {
    const destination = destinationCoordinates(trip);
    destinations.push({
      type: 'Feature',
      id: `destination-${tripId}`,
      geometry: { type: 'Point', coordinates: destination },
      properties: {
        tripId,
        tripName: trip.name || 'Trip',
        label: trip.destination?.label || 'Destination',
        distanceMeters: Number(trip.route?.distanceMeters) || 0,
        durationSeconds: Number(trip.route?.durationSeconds) || 0,
        rerouteCount: Number(trip.rerouteCount) || 0,
      },
    });
    const routeCoordinates = trip.route?.geometry?.coordinates;
    const origin = pointCoordinates(item);
    const geometry = Array.isArray(routeCoordinates) && routeCoordinates.length >= 2
      ? routeCoordinates.filter(validCoordinates).map((coordinate) => coordinate.slice(0, 2).map(Number))
      : origin
        ? [origin, destination]
        : [];
    if (geometry.length >= 2) {
      routes.push({
        type: 'Feature',
        id: `route-${tripId}`,
        geometry: { type: 'LineString', coordinates: geometry },
        properties: { tripId, tripName: trip.name || 'Trip', routeType: 'main' },
      });
    }
    const supportRoutes = [
      ['emergency_response', item.emergencyResponseRoute ?? trip.emergencyResponseRoute],
      ['regroup', item.regroupRoute ?? trip.regroupRoute],
    ];
    for (const [routeType, supportRoute] of supportRoutes) {
      const supportCoordinates = supportRoute?.geometry?.coordinates ?? supportRoute?.coordinates;
      const normalized = Array.isArray(supportCoordinates)
        ? supportCoordinates.filter(validCoordinates).map((coordinate) => coordinate.slice(0, 2).map(Number))
        : [];
      if (normalized.length < 2) continue;
      routes.push({
        type: 'Feature',
        id: `${routeType}-${tripId}`,
        geometry: { type: 'LineString', coordinates: normalized },
        properties: { tripId, tripName: trip.name || 'Trip', routeType },
      });
    }
  }
  return { destinations, routes };
}

function distanceMeters(left, right) {
  const radians = (value) => value * Math.PI / 180;
  const latitude1 = radians(left[1]);
  const latitude2 = radians(right[1]);
  const latitudeDelta = radians(right[1] - left[1]);
  const longitudeDelta = radians(right[0] - left[0]);
  const value = Math.sin(latitudeDelta / 2) ** 2 + Math.cos(latitude1) * Math.cos(latitude2) * Math.sin(longitudeDelta / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
}

function centroid(coordinates) {
  if (!coordinates.length) return null;
  return [
    coordinates.reduce((sum, value) => sum + value[0], 0) / coordinates.length,
    coordinates.reduce((sum, value) => sum + value[1], 0) / coordinates.length,
  ];
}

function circlePolygon(center, radiusMeters, points = 36) {
  const latitudeScale = radiusMeters / 111320;
  const longitudeScale = radiusMeters / (111320 * Math.max(0.2, Math.cos(center[1] * Math.PI / 180)));
  const coordinates = Array.from({ length: points + 1 }, (_, index) => {
    const angle = index / points * Math.PI * 2;
    return [center[0] + Math.cos(angle) * longitudeScale, center[1] + Math.sin(angle) * latitudeScale];
  });
  return { type: 'Polygon', coordinates: [coordinates] };
}

function convoyBubbleFeatures(members) {
  const groups = new Map();
  for (const feature of members) {
    const key = feature.properties.subConvoyId || feature.properties.tripId;
    if (!key) continue;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(feature);
  }
  return [...groups.entries()].flatMap(([key, group]) => {
    if (group.length < 2) return [];
    const coordinates = group.map((feature) => feature.geometry.coordinates);
    const center = centroid(coordinates);
    const radius = Math.max(90, ...coordinates.map((value) => distanceMeters(center, value))) + 65;
    return [{
      type: 'Feature',
      id: `bubble-${key}`,
      geometry: circlePolygon(center, Math.min(radius, 2500)),
      properties: {
        groupKey: key,
        label: group[0].properties.subConvoyName || group[0].properties.tripName || 'Convoy',
        riderCount: group.length,
        clubId: group[0].properties.clubId,
      },
    }];
  });
}

function splitLineFeatures(members) {
  const byTrip = new Map();
  for (const feature of members) {
    if (!feature.properties.tripId) continue;
    if (!byTrip.has(feature.properties.tripId)) byTrip.set(feature.properties.tripId, new Map());
    const groupKey = feature.properties.subConvoyId || 'main';
    const groups = byTrip.get(feature.properties.tripId);
    if (!groups.has(groupKey)) groups.set(groupKey, []);
    groups.get(groupKey).push(feature.geometry.coordinates);
  }
  const results = [];
  for (const [tripId, groups] of byTrip) {
    const centers = [...groups.entries()].map(([key, values]) => ({ key, center: centroid(values) })).filter((item) => item.center);
    for (let leftIndex = 0; leftIndex < centers.length; leftIndex += 1) {
      for (let rightIndex = leftIndex + 1; rightIndex < centers.length; rightIndex += 1) {
        const gap = distanceMeters(centers[leftIndex].center, centers[rightIndex].center);
        if (gap < 500) continue;
        results.push({
          type: 'Feature',
          id: `split-${tripId}-${centers[leftIndex].key}-${centers[rightIndex].key}`,
          geometry: { type: 'LineString', coordinates: [centers[leftIndex].center, centers[rightIndex].center] },
          properties: { tripId, gapMeters: Math.round(gap), label: `${(gap / 1000).toFixed(1)} km split` },
        });
      }
    }
  }
  return results;
}

function ghostTrailFeatures(locations) {
  return locations.flatMap((item) => {
    const value = item?.payload && typeof item.payload === 'object' ? item.payload : item;
    const candidates = [value.recentTrail, value.trail, value.lastReliableTrail];
    const trail = candidates.find((candidate) => Array.isArray(candidate) && candidate.length >= 2);
    if (!trail) return [];
    const coordinates = trail.map((entry) => Array.isArray(entry) ? entry : entry?.coordinates).filter(validCoordinates).map((entry) => entry.slice(0, 2).map(Number));
    if (coordinates.length < 2) return [];
    return [{
      type: 'Feature',
      id: `trail-${value.tripId}-${value.memberId}`,
      geometry: { type: 'LineString', coordinates },
      properties: {
        estimated: value.connectivityStage === 'emergency_suspected' || value.connectivityStage === 'emergency_active',
        memberId: String(value.memberId || ''),
      },
    }];
  });
}

function operationalPointFeatures(locations) {
  const features = [];
  const seen = new Set();
  for (const item of locations) {
    const trip = item.trip || {};
    const tripId = trip.id || String(item.tripId || '');
    const regroup = trip.activeRegroupPoint?.coordinates;
    if (tripId && validCoordinates(regroup) && !seen.has(`regroup-${tripId}`)) {
      seen.add(`regroup-${tripId}`);
      features.push({ type: 'Feature', id: `regroup-${tripId}`, geometry: { type: 'Point', coordinates: regroup }, properties: { pointType: 'regroup', label: trip.activeRegroupPoint?.label || 'Regroup point' } });
    }
    for (const hazard of item.hazards || trip.hazards || []) {
      const coordinates = hazard.location?.coordinates || hazard.coordinates;
      const key = `hazard-${hazard.id || hazard._id || coordinates}`;
      if (!validCoordinates(coordinates) || seen.has(key)) continue;
      seen.add(key);
      features.push({ type: 'Feature', id: key, geometry: { type: 'Point', coordinates }, properties: { pointType: 'hazard', hazardType: hazard.type || 'warning', label: hazard.label || hazard.type || 'Road hazard' } });
    }
  }
  return features;
}

function mapFeatures(locations) {
  const members = memberFeatures(locations);
  const trips = tripFeatures(locations);
  return {
    members,
    ...trips,
    bubbles: convoyBubbleFeatures(members),
    splits: splitLineFeatures(members),
    trails: ghostTrailFeatures(locations),
    operationalPoints: operationalPointFeatures(locations),
  };
}

function setRouteData(instance, features) {
  const collection = (values) => ({ type: 'FeatureCollection', features: values });
  instance.getSource('destinations')?.setData(collection(features.destinations));
  instance.getSource('routes')?.setData(collection(features.routes));
  instance.getSource('member-clusters')?.setData(collection(features.members));
  instance.getSource('convoy-bubbles')?.setData(collection(features.bubbles));
  instance.getSource('split-lines')?.setData(collection(features.splits));
  instance.getSource('ghost-trails')?.setData(collection(features.trails));
  instance.getSource('operational-points')?.setData(collection(features.operationalPoints));
}

function allCoordinates(features) {
  return [
    ...features.members.map((feature) => feature.geometry.coordinates),
    ...features.destinations.map((feature) => feature.geometry.coordinates),
    ...features.routes.flatMap((feature) => feature.geometry.coordinates),
    ...features.trails.flatMap((feature) => feature.geometry.coordinates),
    ...features.splits.flatMap((feature) => feature.geometry.coordinates),
    ...features.operationalPoints.map((feature) => feature.geometry.coordinates),
  ].filter(validCoordinates);
}

function featureSignature(features) {
  return [
    ...features.members.map((feature) => `${feature.id}:${feature.geometry.coordinates.join(',')}`),
    ...features.destinations.map((feature) => feature.id),
    ...features.routes.map((feature) => feature.id),
  ].sort().join('|');
}

function focusActiveFleet(instance, features, lastSignature) {
  const coordinates = allCoordinates(features);
  if (!coordinates.length) return lastSignature;
  const signature = featureSignature(features);
  if (signature === lastSignature) return lastSignature;
  if (coordinates.length === 1) {
    instance.easeTo({ center: coordinates[0], zoom: 14, pitch: 28, duration: 600 });
    return signature;
  }
  const bounds = coordinates.reduce(
    (value, coordinate) => value.extend(coordinate),
    new maplibregl.LngLatBounds(coordinates[0], coordinates[0]),
  );
  instance.fitBounds(bounds, { padding: 68, maxZoom: 15, duration: 650 });
  return signature;
}

function markerTone(properties) {
  const visual = markerVisual(properties, readMapIdentity());
  if (visual.state === 'critical') return 'critical';
  if (visual.state === 'warning') return 'warning';
  if (visual.state === 'offline') return 'offline';
  return visual.role.tone;
}

function readMapIdentity() {
  try {
    return JSON.parse(window.localStorage.getItem('convoy.mapIdentity') || '{}');
  } catch {
    return {};
  }
}

function applyClubRing(element, properties) {
  const visual = markerVisual(properties, readMapIdentity());
  element.style.setProperty('--club-ring', visual.club.color);
  element.dataset.clubId = String(properties.clubId || '');
  element.dataset.clubPattern = properties.clubPattern || visual.club.pattern;
}

function updateMarkerVisual(element, properties) {
  const visual = markerVisual(properties, readMapIdentity());
  const battery = Number(properties.batteryPercent);
  const batteryBadge = Number.isFinite(battery) && battery >= 0 && battery <= 20
    ? `<span class="tactical-rider-marker__badge">${Math.round(battery)}</span>`
    : '';
  const label = properties.memberName ? `<span class="tactical-rider-marker__label">${properties.memberName}</span>` : '';
  element.className = `tactical-rider-marker tactical-rider-marker--${markerTone(properties)} tactical-rider-marker--state-${visual.state}`;
  element.style.setProperty('--marker-heading', `${Number(properties.heading || 0)}deg`);
  element.innerHTML = `<span class="tactical-rider-marker__pulse"></span><span class="tactical-rider-marker__direction"></span><span class="tactical-rider-marker__ring"></span><span class="tactical-rider-marker__core">${visual.glyph}</span>${batteryBadge}${label}`;
  applyClubRing(element, properties);
}

function markerElement(feature) {
  const element = document.createElement('button');
  element.type = 'button';
  updateMarkerVisual(element, feature.properties);
  element.setAttribute('aria-label', feature.properties.memberName);
  return element;
}

function markerPopup(feature) {
  const properties = feature.properties;
  const coordinates = feature.geometry.coordinates;
  const battery = Number(properties.batteryPercent);
  return new maplibregl.Popup({ offset: 18 }).setHTML(
    `<strong>${properties.memberName}</strong><br/>${Number(coordinates[1]).toFixed(6)}, ${Number(coordinates[0]).toFixed(6)}<br/>Trip: ${properties.tripName || '-'}<br/>Destination: ${properties.destinationLabel || '-'}<br/>Speed ${Math.round(properties.speedKmh || 0)} km/h<br/>Battery ${battery >= 0 ? `${Math.round(battery)}%${properties.isCharging ? ' ⚡' : ''}` : '-'}<br/>Connectivity: ${String(properties.connectivityStage || 'online').replaceAll('_', ' ')}`,
  );
}

function applyMarkerDensity(instance, markers) {
  const zoom = instance.getZoom();
  for (const record of markers.values()) {
    const role = normalizedRideRole(record.properties?.rideRole);
    const state = markerVisual(record.properties || {}, readMapIdentity()).state;
    const priority = ['organizer', 'road_captain', 'sweeper', 'medic', 'mechanic'].includes(role)
      || ['critical', 'warning', 'offline'].includes(state);
    record.element.style.display = zoom >= 11 || priority ? '' : 'none';
    record.element.classList.toggle('tactical-rider-marker--detailed', zoom >= 14);
  }
}

function syncMemberMarkers(instance, features, markers) {
  const activeIds = new Set();
  for (const feature of features.members) {
    const id = String(feature.id);
    activeIds.add(id);
    let record = markers.get(id);
    if (!record) {
      const element = markerElement(feature);
      const marker = new maplibregl.Marker({ element, anchor: 'center' })
        .setLngLat(feature.geometry.coordinates)
        .setPopup(markerPopup(feature))
        .addTo(instance);
      record = { marker, element, tone: markerTone(feature.properties), properties: feature.properties };
      markers.set(id, record);
    } else {
      record.marker.setLngLat(feature.geometry.coordinates);
      const tone = markerTone(feature.properties);
      if (tone !== record.tone || record.element.dataset.clubId !== String(feature.properties.clubId || '') || Number(record.properties?.heading || 0) !== Number(feature.properties.heading || 0) || record.properties?.isSpeaking !== feature.properties.isSpeaking || record.properties?.batteryPercent !== feature.properties.batteryPercent) {
        updateMarkerVisual(record.element, feature.properties);
        record.tone = tone;
        record.properties = feature.properties;
      }
      record.marker.setPopup(markerPopup(feature));
    }
  }
  for (const [id, record] of markers) {
    if (activeIds.has(id)) continue;
    record.marker.remove();
    markers.delete(id);
  }
  applyMarkerDensity(instance, markers);
}

function installOperationalLayers(instance) {
  const addSource = (id, options = {}) => {
    if (!instance.getSource(id)) instance.addSource(id, { type: 'geojson', data: emptyCollection(), ...options });
  };
  addSource('routes');
  addSource('destinations');
  addSource('member-clusters', { cluster: true, clusterMaxZoom: 11, clusterRadius: 48 });
  addSource('convoy-bubbles');
  addSource('split-lines');
  addSource('ghost-trails');
  addSource('operational-points');

  if (!instance.getLayer('convoy-bubbles')) instance.addLayer({
    id: 'convoy-bubbles', type: 'fill', source: 'convoy-bubbles',
    paint: { 'fill-color': '#42e8e0', 'fill-opacity': 0.055 },
  });
  if (!instance.getLayer('convoy-bubble-outline')) instance.addLayer({
    id: 'convoy-bubble-outline', type: 'line', source: 'convoy-bubbles',
    paint: { 'line-color': '#42e8e0', 'line-width': 1.2, 'line-opacity': 0.28, 'line-dasharray': [3, 3] },
  });
  if (!instance.getLayer('route-shadow')) instance.addLayer({
    id: 'route-shadow', type: 'line', source: 'routes',
    paint: {
      'line-width': 9,
      'line-color': ['match', ['get', 'routeType'], 'emergency_response', '#21070b', 'regroup', '#201604', '#01060b'],
      'line-opacity': 0.82,
    },
  });
  if (!instance.getLayer('routes')) instance.addLayer({
    id: 'routes', type: 'line', source: 'routes',
    paint: {
      'line-width': ['match', ['get', 'routeType'], 'emergency_response', 5.5, 'regroup', 4, 4.5],
      'line-color': ['match', ['get', 'routeType'], 'emergency_response', '#f97066', 'regroup', '#f4b942', '#42e8e0'],
      'line-opacity': 0.98,
    },
  });
  if (!instance.getLayer('ghost-trails')) instance.addLayer({
    id: 'ghost-trails', type: 'line', source: 'ghost-trails',
    paint: {
      'line-width': 3,
      'line-color': ['case', ['get', 'estimated'], '#f4b942', '#60a5fa'],
      'line-opacity': 0.65,
      'line-dasharray': [2, 2],
    },
  });
  if (!instance.getLayer('split-lines')) instance.addLayer({
    id: 'split-lines', type: 'line', source: 'split-lines',
    paint: { 'line-width': 2.2, 'line-color': '#f4b942', 'line-opacity': 0.82, 'line-dasharray': [2, 2] },
  });
  if (!instance.getLayer('split-labels')) instance.addLayer({
    id: 'split-labels', type: 'symbol', source: 'split-lines',
    layout: { 'symbol-placement': 'line-center', 'text-field': ['get', 'label'], 'text-size': 10 },
    paint: { 'text-color': '#fde68a', 'text-halo-color': '#061019', 'text-halo-width': 2 },
  });
  if (!instance.getLayer('destinations')) instance.addLayer({
    id: 'destinations', type: 'circle', source: 'destinations',
    paint: { 'circle-radius': 9, 'circle-color': '#F4B942', 'circle-stroke-width': 3, 'circle-stroke-color': '#070A0F' },
  });
  if (!instance.getLayer('operational-point-halo')) instance.addLayer({
    id: 'operational-point-halo', type: 'circle', source: 'operational-points',
    paint: {
      'circle-radius': ['case', ['==', ['get', 'pointType'], 'regroup'], 18, 14],
      'circle-color': ['case', ['==', ['get', 'pointType'], 'regroup'], '#42e8e0', '#f4b942'],
      'circle-opacity': 0.14,
      'circle-stroke-width': 1.5,
      'circle-stroke-color': ['case', ['==', ['get', 'pointType'], 'regroup'], '#42e8e0', '#f4b942'],
    },
  });
  if (!instance.getLayer('operational-point-core')) instance.addLayer({
    id: 'operational-point-core', type: 'circle', source: 'operational-points',
    paint: {
      'circle-radius': 6,
      'circle-color': ['case', ['==', ['get', 'pointType'], 'regroup'], '#42e8e0', '#f4b942'],
      'circle-stroke-width': 3,
      'circle-stroke-color': '#061019',
    },
  });
  if (!instance.getLayer('member-cluster-halo')) instance.addLayer({
    id: 'member-cluster-halo', type: 'circle', source: 'member-clusters', filter: ['has', 'point_count'],
    paint: { 'circle-radius': ['step', ['get', 'point_count'], 24, 12, 31, 30, 38], 'circle-color': '#42e8e0', 'circle-opacity': 0.1, 'circle-stroke-color': '#42e8e0', 'circle-stroke-width': 1.5 },
  });
  if (!instance.getLayer('member-clusters')) instance.addLayer({
    id: 'member-clusters', type: 'circle', source: 'member-clusters', filter: ['has', 'point_count'],
    paint: { 'circle-radius': ['step', ['get', 'point_count'], 16, 12, 21, 30, 27], 'circle-color': '#07141d', 'circle-stroke-color': '#67e8f9', 'circle-stroke-width': 2 },
  });
  if (!instance.getLayer('member-cluster-count')) instance.addLayer({
    id: 'member-cluster-count', type: 'symbol', source: 'member-clusters', filter: ['has', 'point_count'],
    layout: { 'text-field': ['concat', ['get', 'point_count_abbreviated'], '\nRIDERS'], 'text-size': 9, 'text-font': ['Open Sans Bold'] },
    paint: { 'text-color': '#d9ffff' },
  });
}

function TacticalCoordinateFallback({ features, visible }) {
  if (!visible) return null;
  const coordinates = allCoordinates(features);
  const longitudeValues = coordinates.map((value) => value[0]);
  const latitudeValues = coordinates.map((value) => value[1]);
  const minLongitude = Math.min(...longitudeValues, 106.7);
  const maxLongitude = Math.max(...longitudeValues, 106.9);
  const minLatitude = Math.min(...latitudeValues, -6.3);
  const maxLatitude = Math.max(...latitudeValues, -6.1);
  const longitudeSpan = Math.max(0.002, maxLongitude - minLongitude);
  const latitudeSpan = Math.max(0.002, maxLatitude - minLatitude);
  const project = ([longitude, latitude]) => [
    8 + ((longitude - minLongitude) / longitudeSpan) * 84,
    92 - ((latitude - minLatitude) / latitudeSpan) * 84,
  ];
  return <div className="absolute inset-0 z-0 overflow-hidden bg-[#040810]">
    <div className="absolute inset-0 opacity-55 [background-image:linear-gradient(rgba(66,232,224,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(66,232,224,.08)_1px,transparent_1px)] [background-size:48px_48px]" />
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-label="Live rider coordinate fallback">
      {features.routes.map((feature) => <polyline
        key={feature.id}
        points={feature.geometry.coordinates.map((value) => project(value).join(',')).join(' ')}
        fill="none"
        stroke="#42e8e0"
        strokeWidth="0.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity="0.92"
      />)}
      {features.destinations.map((feature) => {
        const [x, y] = project(feature.geometry.coordinates);
        return <g key={feature.id}>
          <circle cx={x} cy={y} r="2.3" fill="#f4b942" opacity="0.22" />
          <circle cx={x} cy={y} r="0.9" fill="#f4b942" stroke="#f5f8fc" strokeWidth="0.35" />
        </g>;
      })}
      {features.members.map((feature) => {
        const [x, y] = project(feature.geometry.coordinates);
        const tone = markerTone(feature.properties);
        const color = {
          critical: '#f97066', warning: '#f4b942', offline: '#7b8798',
          leader: '#42e8e0', organizer: '#42e8e0', road_captain: '#42e8e0', club_captain: '#a78bfa', sub_convoy_leader: '#60a5fa', sweeper: '#f4b942', medic: '#34d399', mechanic: '#f59e0b', marshal: '#38bdf8', guest: '#94a3b8', member: '#6f7dff',
        }[tone];
        return <g key={feature.id}>
          <circle cx={x} cy={y} r="2.8" fill={color} opacity="0.14" />
          <circle cx={x} cy={y} r="1.15" fill={color} stroke="#f5f8fc" strokeWidth="0.45" />
        </g>;
      })}
    </svg>
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 border border-cyan-300/15 bg-[#03070d]/70 px-3 py-2 text-center backdrop-blur-sm">
      <p className="text-[8px] font-black uppercase tracking-[.18em] text-cyan-300">Coordinate tactical view</p>
      <p className="mt-1 text-[8px] text-slate-500">Live riders and routes remain visible without map tiles.</p>
    </div>
  </div>;
}

export function LiveMap({ locations, className = 'h-[calc(100vh-9rem)] min-h-130 w-full overflow-hidden border border-white/8' }) {
  const container = useRef(null);
  const map = useRef();
  const markers = useRef(new Map());
  const latestLocations = useRef(locations);
  const lastFitSignature = useRef('');
  const fallbackApplied = useRef(!useCustomStyle);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState('');
  const [coordinateFallback, setCoordinateFallback] = useState(false);
  latestLocations.current = Array.isArray(locations) ? locations : [];

  useEffect(() => {
    if (!container.current || map.current) return undefined;
    const instance = new maplibregl.Map({
      container: container.current,
      style: initialStyle,
      center: [106.8456, -6.2088],
      zoom: 10,
      pitch: 28,
      attributionControl: true,
      failIfMajorPerformanceCaveat: false,
    });
    map.current = instance;
    const resizeObserver = new ResizeObserver(() => instance.resize());
    resizeObserver.observe(container.current);
    instance.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');

    const renderFleet = () => {
      const features = mapFeatures(latestLocations.current);
      try {
        installOperationalLayers(instance);
        setRouteData(instance, features);
      } catch (cause) {
        setMapError(cause instanceof Error ? cause.message : String(cause));
      }
      syncMemberMarkers(instance, features, markers.current);
      lastFitSignature.current = focusActiveFleet(instance, features, lastFitSignature.current);
      setMapReady(true);
      window.requestAnimationFrame(() => instance.resize());
    };

    const switchToFallback = () => {
      if (fallbackApplied.current) return;
      fallbackApplied.current = true;
      setMapError('Configured map style failed. Built-in tactical tiles are active.');
      setCoordinateFallback(false);
      instance.setStyle(tacticalRasterStyle);
    };

    const fallbackTimer = window.setTimeout(() => {
      if (!instance.loaded()) {
        if (useCustomStyle) switchToFallback();
        else {
          setMapError('Map tiles unavailable. Coordinate fallback is active.');
          setCoordinateFallback(true);
          setMapReady(true);
        }
      }
    }, 6_000);

    instance.on('styleimagemissing', (event) => {
      if (event.id !== 'wood-pattern' || instance.hasImage(event.id)) return;
      instance.addImage(event.id, woodPatternImage);
    });
    instance.on('style.load', renderFleet);
    instance.on('load', renderFleet);
    instance.on('error', (event) => {
      const message = event?.error?.message || '';
      if (message) setMapError(message);
      const tileFailure = /tile|source|network|fetch|cors/i.test(message);
      if (useCustomStyle && !fallbackApplied.current && !instance.loaded()) {
        switchToFallback();
      } else if (tileFailure) {
        setMapError('Map tiles unavailable. Coordinate fallback is active.');
        setCoordinateFallback(true);
        setMapReady(true);
      }
    });
    instance.on('idle', () => {
      if (!fallbackApplied.current || !instance.getSource('tacticalTiles')) return;
      if (instance.isSourceLoaded('tacticalTiles')) {
        setCoordinateFallback(false);
        setMapError('');
      }
    });
    instance.on('click', 'member-clusters', async (event) => {
      const feature = event.features?.[0];
      const clusterId = feature?.properties?.cluster_id;
      if (clusterId == null || feature.geometry.type !== 'Point') return;
      const source = instance.getSource('member-clusters');
      const zoom = await source.getClusterExpansionZoom(clusterId);
      instance.easeTo({ center: feature.geometry.coordinates, zoom, duration: 500 });
    });
    instance.on('mouseenter', 'member-clusters', () => { instance.getCanvas().style.cursor = 'pointer'; });
    instance.on('mouseleave', 'member-clusters', () => { instance.getCanvas().style.cursor = ''; });
    instance.on('zoomend', () => applyMarkerDensity(instance, markers.current));

    instance.on('click', 'destinations', (event) => {
      const feature = event.features?.[0];
      if (!feature || feature.geometry.type !== 'Point') return;
      const distanceKm = Number(feature.properties?.distanceMeters || 0) / 1000;
      const durationMinutes = Math.ceil(Number(feature.properties?.durationSeconds || 0) / 60);
      new maplibregl.Popup()
        .setLngLat(feature.geometry.coordinates)
        .setHTML(`<strong>${feature.properties?.label}</strong><br/>Trip: ${feature.properties?.tripName}<br/>Route: ${distanceKm > 0 ? `${distanceKm.toFixed(1)} km` : '-'} · ${durationMinutes > 0 ? `${durationMinutes} min` : '-'}`)
        .addTo(instance);
    });

    return () => {
      window.clearTimeout(fallbackTimer);
      resizeObserver.disconnect();
      for (const record of markers.current.values()) record.marker.remove();
      markers.current.clear();
      instance.remove();
      map.current = undefined;
    };
  }, []);

  useEffect(() => {
    const instance = map.current;
    if (!instance || !mapReady) return;
    const features = mapFeatures(latestLocations.current);
    try {
      setRouteData(instance, features);
    } catch {
      // Style may be switching to the built-in fallback; style.load will replay the data.
    }
    syncMemberMarkers(instance, features, markers.current);
    lastFitSignature.current = focusActiveFleet(instance, features, lastFitSignature.current);
  }, [locations, mapReady]);

  const currentFeatures = mapFeatures(latestLocations.current);
  const signalCount = currentFeatures.members.length;
  const invalidSignalCount = Math.max(0, latestLocations.current.length - signalCount);
  return <div className={`relative isolate bg-[#040810] ${className}`}>
    <TacticalCoordinateFallback features={currentFeatures} visible={coordinateFallback} />
    <div ref={container} className={`absolute inset-0 z-0 transition-opacity duration-300 ${coordinateFallback ? 'opacity-0' : 'opacity-100'}`} />
    <div className="pointer-events-none absolute inset-0 z-[1] bg-[linear-gradient(rgba(66,232,224,.035)_1px,transparent_1px),linear-gradient(90deg,rgba(66,232,224,.035)_1px,transparent_1px)] bg-[size:32px_32px] opacity-45" />
    {mapReady && signalCount === 0 && <div className="pointer-events-none absolute inset-0 z-[2] grid place-items-center">
      <div className="border border-cyan-300/20 bg-[#03070d]/85 px-4 py-3 text-center backdrop-blur-xl">
        <p className="text-[9px] font-black uppercase tracking-[.18em] text-cyan-300">Waiting for rider signal</p>
        <p className="mt-1 text-[10px] text-slate-400">{invalidSignalCount > 0 ? `${invalidSignalCount} live record(s) received without valid coordinates.` : 'Start a trip and allow background location.'}</p>
      </div>
    </div>}
    {mapError && <div className="pointer-events-none absolute bottom-3 left-3 z-[3] max-w-sm border border-amber-300/20 bg-[#100b03]/88 px-3 py-2 text-[9px] text-amber-200 backdrop-blur-xl">
      {coordinateFallback ? 'Map tiles unavailable · coordinate fallback active' : `Map fallback: ${mapError}`}
    </div>}
  </div>;
}
